import moment from "moment";
import { computeAgendaStatuses, getStarttime } from "./timeCalculations";
import { translate } from "../i18n/messages";

// Live adjustments while a plan is running. Everything lives in the URL
// like the plan itself:
// - pausedat: when the timer was paused. While it is set, the clock the
//   plan is measured against stands still at that moment.
// - drift: seconds the plan has moved compared to how it was started
//   (pauses and extensions add, finishing a card early subtracts), so the
//   app can say how far behind or ahead of the original plan the session is.
// Each function takes the current params ({ starttime, agenda, ... } as
// strings, like CreateParamsJson returns them) and returns new ones.

// The moment the plan is measured against: now, or the pause moment.
export function planClock(params, now = moment()) {
  return params.pausedat ? moment(params.pausedat) : now;
}

function readDrift(params) {
  return Number(params.drift) || 0;
}

function withDrift(params, seconds) {
  const drift = Math.round(readDrift(params) + seconds);
  // 0 is dropped from the URL by concatParams, which is what we want.
  return { ...params, drift: drift ? String(drift) : undefined };
}

function secondsToHHmmss(totalSeconds) {
  const pad = (n) => String(n).padStart(2, "0");
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// Index of the card running at the plan clock, or -1.
export function activeIndex(params, now = moment()) {
  if (!params.starttime || !params.agenda) {
    return -1;
  }
  const agenda = JSON.parse(params.agenda);
  return computeAgendaStatuses(
    agenda,
    moment(params.starttime),
    planClock(params, now)
  ).indexOf("active");
}

export function pause(params, now = moment()) {
  if (params.pausedat) {
    return params;
  }
  return { ...params, pausedat: now.toISOString() };
}

// Moves the whole plan by the time spent paused, so every card keeps the
// time it had left when the pause began.
export function resume(params, now = moment()) {
  if (!params.pausedat) {
    return params;
  }
  const pausedSeconds = Math.max(
    0,
    now.diff(moment(params.pausedat), "seconds")
  );
  const starttime = moment(params.starttime)
    .add(pausedSeconds, "seconds")
    .toISOString();
  return withDrift(
    { ...params, starttime, pausedat: undefined },
    pausedSeconds
  );
}

// Gives the running card more (or, with negative seconds, less) time.
export function extendActive(params, seconds, now = moment()) {
  const index = activeIndex(params, now);
  if (index === -1) {
    return params;
  }
  const agenda = JSON.parse(params.agenda);
  const current = moment.duration(agenda[index].time).asSeconds();
  const elapsed = planClock(params, now).diff(
    getStarttime(agenda.slice(0, index + 1), moment(params.starttime)),
    "seconds"
  );
  // Never shrink a card below the time it has already run (plus a second,
  // so it is still the running card afterwards).
  const next = Math.max(current + seconds, elapsed + 1, 1);
  agenda[index] = { ...agenda[index], time: secondsToHHmmss(next) };
  return withDrift(
    { ...params, agenda: JSON.stringify(agenda) },
    next - current
  );
}

// Ends the running card now and ticks it off; the next card starts at once.
export function finishActive(params, now = moment()) {
  const index = activeIndex(params, now);
  if (index === -1) {
    return params;
  }
  const agenda = JSON.parse(params.agenda);
  const current = moment.duration(agenda[index].time).asSeconds();
  const elapsed = Math.max(
    1,
    Math.floor(
      planClock(params, now).diff(
        getStarttime(agenda.slice(0, index + 1), moment(params.starttime)),
        "seconds"
      )
    )
  );
  agenda[index] = {
    ...agenda[index],
    time: secondsToHHmmss(elapsed),
    done: true,
  };
  return withDrift(
    { ...params, agenda: JSON.stringify(agenda) },
    elapsed - current
  );
}

// Clears the live state, e.g. when the plan is restarted.
export function clearLiveState(params) {
  return { ...params, pausedat: undefined, drift: undefined };
}

// "8 min behind plan", "3 min ahead of plan", "On plan" - or null when the
// plan hasn't been adjusted live at all.
export function describeDrift(params, language = "en") {
  if (params.drift === undefined) {
    return null;
  }
  const minutes = Math.round(readDrift(params) / 60);
  if (minutes > 0) {
    return translate(language, "live.behind", { minutes });
  }
  if (minutes < 0) {
    return translate(language, "live.ahead", { minutes: -minutes });
  }
  return translate(language, "live.onPlan");
}
